/** Read-only summary of setup information for the authenticated company. */
export type TenantHealthStatus = "ready" | "attention" | "failing" | "unknown" | "notApplicable";

/** Roll-up ranking, most severe first: failing > attention > unknown > ready. notApplicable never ranks. */
export const TENANT_HEALTH_STATUS_RANK: readonly TenantHealthStatus[] = ["failing", "attention", "unknown", "ready"];

/**
 * The single roll-up semantics for health statuses (backend and frontend).
 * Unknown is never reported as ready. An empty list, or a list holding only
 * notApplicable, rolls up to notApplicable.
 */
export function worstTenantHealthStatus(statuses: readonly TenantHealthStatus[]): TenantHealthStatus {
  for (const status of TENANT_HEALTH_STATUS_RANK) if (statuses.includes(status)) return status;
  return "notApplicable";
}

export type TenantHealthVerification = "verified" | "notVerified" | "notConnected" | "unknown" | "notConfigured";

export interface TenantHealthItem {
  id: string;
  name: string;
  /** Stable issue keys for UI localization; never contains raw server errors. */
  issues?: string[];
  fixHref?: string;
}

export interface TenantHealthCheck {
  id: string;
  status: TenantHealthStatus;
  /** Number of affected or configured records, depending on the check. */
  count?: number;
  /** Stable code for translated contextual copy. */
  detail?: string;
  verification?: TenantHealthVerification;
  fixHref?: string;
  items?: TenantHealthItem[];
  /** ISO timestamps of the last known success or failure of a runtime signal. */
  lastSuccessAt?: string;
  lastFailureAt?: string;
  /** Whitelisted, non-secret values only (for example stripeMode, version). */
  meta?: Record<string, string | number | boolean>;
}

export interface TenantHealthCategory {
  id: "company" | "integrations" | "tripsBooking" | "logbook";
  checks: TenantHealthCheck[];
}

export interface TenantHealthOverview {
  checkedAt: string;
  categories: TenantHealthCategory[];
}

export type OpsHealthCategoryId = "platform" | "integrations" | "jobs" | "bookings" | "vessel";

/** Operational (runtime) health for the authenticated company's deployment. */
export interface OpsHealthResponse {
  checkedAt: string;
  cachedUntil: string;
  categories: { id: OpsHealthCategoryId; checks: TenantHealthCheck[] }[];
}
